import { useEffect, useMemo, useState } from "react";
import { Instagram, Mail, ExternalLink, X } from "lucide-react";
import { supabase } from "../lib/supabase";
import { MobilePageTitle } from "../components/MobilePageTitle";
import {
  BLOQUE_ESQUELETO,
  EsqueletoSeccion,
  claseContenido,
} from "../components/EsqueletoSeccion";
import { useUserGroup } from "../hooks/useUserGroup";

interface Video {
  id: number;
  categoria: string;
  titulo: string;
  link: string;
  created_at: string;
}

// Solo se ven los tutoriales del flujo V2, del 30/05/2026 en adelante. Los viejos
// explican el flujo clásico, borrado en ADR-2026-10-28-se-borra-el-flujo-clasico;
// los dev testers los siguen viendo todos.
// `created_at` es ISO ("YYYY-MM-DD…") tanto con 'T' como con espacio, así que la
// comparación de strings contra la fecha (primeros 10 chars) es suficiente.
const TUTORIALES_V2_DESDE = "2026-05-30";

export const Ayuda = () => {
  const { isDevTester } = useUserGroup();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState<"instagram" | "whatsapp" | null>(null);

  const instagramUrl = "https://www.instagram.com/estudioatlas_";
  const whatsappUrl = "https://wa.me/5491122635392";
  const instagramQR = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(instagramUrl)}`;
  const whatsappQR = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(whatsappUrl)}`;

  const isDesktop =
    typeof window !== "undefined" &&
    window.matchMedia("(min-width: 768px)").matches;

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const { data, error } = await supabase
          .from("videos")
          .select("*")
          .order("categoria", { ascending: true });

        if (error) {
          throw error;
        }

        setVideos(data || []);
      } catch (error) {
        console.error("Error fetching videos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const visibleVideos = useMemo(() => {
    if (isDevTester === true) return videos;
    return videos.filter((v) => (v.created_at ?? "") >= TUTORIALES_V2_DESDE);
  }, [videos, isDevTester]);

  const videosByCategory = useMemo(
    () =>
      visibleVideos.reduce((acc: Record<string, Video[]>, video) => {
        if (!acc[video.categoria]) acc[video.categoria] = [];
        acc[video.categoria].push(video);
        return acc;
      }, {}),
    [visibleVideos],
  );

  // Function to get YouTube video ID from URL
  const getYouTubeVideoId = (url: string): string | null => {
    const regex =
      /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  return (
    <>
      {/* Mientras cargan los videos, la pantalla entera va en esqueleto y entra de
          una vez, como el resto de las secciones
          (ADR-2026-08-03-todas-las-secciones-cargan-igual). Antes la tarjeta de
          contacto se dibujaba enseguida y abajo giraba un spinner.
          Las clases de la fila del título y de la raíz son las mismas de abajo. */}
      {loading && (
        <EsqueletoSeccion
          className="px-4 sm:px-8 pb-8 max-w-content mx-auto"
          claseFilaTitulo="hidden sm:flex items-center justify-between mb-5 h-11"
          anchoTitulo="w-[110px]"
        >
          {/* Tarjeta de contacto: título + tres accesos en fila. */}
          <div className="mb-5 rounded-2xl border border-black/[0.06] bg-white p-6">
            <div className={`h-[26px] w-[100px] mb-4 ${BLOQUE_ESQUELETO}`} />
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className={`h-[68px] ${BLOQUE_ESQUELETO}`} />
              ))}
            </div>
          </div>
          {/* Tarjeta de videos: título + una grilla de tres. */}
          <div className="rounded-2xl border border-black/[0.06] bg-white p-6">
            <div className={`h-[26px] w-[140px] mb-4 ${BLOQUE_ESQUELETO}`} />
            <div className={`h-[17px] w-[90px] mb-3 ${BLOQUE_ESQUELETO}`} />
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-xl border border-black/[0.06]"
                >
                  <div className="aspect-video animate-pulse bg-black/[0.06]" />
                  <div className="p-3">
                    <div className={`h-[23px] w-[75%] ${BLOQUE_ESQUELETO}`} />
                    <div className={`mt-1.5 h-[20px] w-[45%] ${BLOQUE_ESQUELETO}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </EsqueletoSeccion>
      )}
    <div
      className={`px-4 sm:px-8 pb-8 max-w-content mx-auto ${claseContenido(!loading)}`}
    >
      {/* Título mobile (desktop usa el header de abajo) */}
      <MobilePageTitle>Ayuda</MobilePageTitle>
      {/* Desktop header */}
      <div className="hidden sm:flex items-center justify-between mb-5 h-11">
        <h1 className="text-[17px] font-semibold text-brand-black/90 mt-4">
          Ayuda
        </h1>
      </div>

      <div className="mb-5 rounded-2xl border border-black/[0.06] bg-white p-4 sm:p-6">
        <h2 className="text-[17px] font-semibold text-brand-black/90 mb-4">
          Contacto
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Instagram */}
          <button
            type="button"
            onClick={() => {
              if (isDesktop) {
                setShowQR("instagram");
              } else {
                window.open(instagramUrl, "_blank");
              }
            }}
            className="group flex w-full items-center gap-3 rounded-xl border border-black/[0.06] p-3 text-left transition-colors hover:bg-black/[0.03]"
          >
            <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 text-white bg-gradient-to-tr from-purple-500 via-pink-500 to-yellow-500 md:text-neutral-600 md:bg-none md:bg-neutral-100 md:group-hover:bg-gradient-to-tr md:group-hover:from-purple-500 md:group-hover:via-pink-500 md:group-hover:to-yellow-500 md:group-hover:text-white transition-colors duration-500 ease-out">
              <Instagram className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-medium text-brand-black/90">
                Instagram
              </p>
              <p className="text-[13px] text-neutral-500">@estudioatlas_</p>
            </div>
            <ExternalLink className="w-4 h-4 text-neutral-400 shrink-0" />
          </button>

          {/* WhatsApp */}
          <button
            type="button"
            onClick={() => {
              if (isDesktop) {
                setShowQR("whatsapp");
              } else {
                window.open(whatsappUrl, "_blank");
              }
            }}
            className="group flex w-full items-center gap-3 rounded-xl border border-black/[0.06] p-3 text-left transition-colors hover:bg-black/[0.03]"
          >
            <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-[#25D366] text-white md:bg-neutral-100 md:text-neutral-600 md:group-hover:bg-[#25D366] md:group-hover:text-white transition-colors duration-500 ease-out">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.0781 4.9151C17.1693 2.99084 14.6008 1.93602 11.868 1.93945C6.25977 1.93945 1.70409 6.49408 1.7002 12.1084C1.69925 13.8729 2.15811 15.6037 2.99707 17.1318L1.60547 22.4004L7.00879 21.0381C8.48438 21.8008 10.1602 22.2 11.8633 22.2002H11.8691C17.4756 22.2002 22.0322 17.6445 22.0361 12.0293C22.0381 9.30713 20.9863 6.83936 19.0781 4.9151ZM11.8682 20.5254H11.8633C10.3457 20.5254 8.85645 20.1484 7.55371 19.4424L7.24609 19.2686L4.12305 20.1025L4.97266 17.0557L4.7793 16.7334C3.99023 15.375 3.57031 13.7646 3.57129 12.1094C3.57422 7.52832 7.29004 3.81543 11.873 3.81543C14.1475 3.81055 16.2803 4.6875 17.877 6.29639C19.4736 7.90527 20.3447 10.0459 20.3438 12.3281C20.3398 16.9102 16.624 20.5254 11.8682 20.5254ZM16.5898 14.2168C16.3389 14.0908 15.1201 13.4922 14.8896 13.4077C14.6592 13.3232 14.4922 13.2812 14.3242 13.5333C14.1562 13.7854 13.6816 14.3413 13.5322 14.5088C13.3828 14.6762 13.2344 14.6967 12.9834 14.5708C12.7324 14.4448 11.9302 14.1885 10.9932 13.3525C10.2666 12.7041 9.77832 11.9102 9.62891 11.6582C9.47949 11.4062 9.61426 11.2671 9.7432 11.1392C9.85742 11.0244 9.99609 10.8384 10.125 10.689C10.2539 10.5396 10.2969 10.4346 10.3809 10.2666C10.4648 10.0986 10.4219 9.94921 10.3594 9.82323C10.2969 9.69726 9.81152 8.47364 9.60254 7.96973C9.39844 7.47949 9.19141 7.54196 9.03418 7.53417C8.88477 7.52734 8.71777 7.52539 8.54980 7.52539C8.38184 7.52539 8.10742 7.58789 7.87695 7.83986C7.64648 8.09183 7.00586 8.69043 7.00586 9.91308C7.00586 11.1357 7.89844 12.3164 8.02734 12.4844C8.15625 12.6523 9.77637 15.1104 12.2227 16.1816C12.8281 16.4414 13.2998 16.5977 13.667 16.7158C14.2852 16.9102 14.8477 16.8828 15.292 16.8193C15.791 16.749 16.7783 16.2207 16.9873 15.6318C17.1963 15.043 17.1963 14.5391 17.1338 14.5088C17.0713 14.4785 16.8408 14.3428 16.5898 14.2168Z"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-medium text-brand-black/90">
                WhatsApp
              </p>
              <p className="text-[13px] text-neutral-500">+54 9 11 2263-5392</p>
            </div>
            <ExternalLink className="w-4 h-4 text-neutral-400 shrink-0" />
          </button>

          {/* Email */}
          <a
            href="mailto:atlas.ia.contacto@gmail.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-xl border border-black/[0.06] p-3 transition-colors hover:bg-black/[0.03]"
          >
            <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-600">
              <Mail className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-medium text-brand-black/90">
                Email
              </p>
              <p className="text-[13px] text-neutral-500">
                atlas.ia.contacto@gmail.com
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-neutral-400 shrink-0" />
          </a>
        </div>
      </div>

      {/* Sin `loading` en la condición: mientras carga, esta rama ni se ve — la tapa
          el esqueleto de arriba. Queda sólo la pregunta que importa acá: si hay
          videos que mostrar. */}
      {visibleVideos.length > 0 && (
        <div className="rounded-2xl border border-black/[0.06] bg-white p-4 sm:p-6">
          <h2 className="text-[17px] font-semibold text-brand-black/90 mb-4">
            Videos de ayuda
          </h2>

          <div className="space-y-6">
              {Object.entries(videosByCategory).map(
                ([category, categoryVideos]) => (
                  <div key={category} className="space-y-3">
                    <h3 className="text-[13px] font-semibold text-neutral-500 uppercase tracking-wide">
                      {category}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categoryVideos.map((video) => {
                        const videoId = getYouTubeVideoId(video.link);
                        return (
                          <div
                            key={video.id}
                            className="overflow-hidden rounded-xl border border-black/[0.06] bg-white"
                          >
                            {videoId && (
                              <div className="aspect-w-16 aspect-h-9">
                                <iframe
                                  src={`https://www.youtube.com/embed/${videoId}`}
                                  title={video.titulo}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  className="w-full h-full"
                                ></iframe>
                              </div>
                            )}
                            <div className="p-3">
                              <h4 className="text-[15px] font-medium text-brand-black/90">
                                {video.titulo}
                              </h4>
                              <a
                                href={video.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-brand-primary text-[13px] flex items-center gap-1 mt-1.5 hover:opacity-70 transition-opacity"
                              >
                                Ver en YouTube{" "}
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ),
              )}
          </div>
        </div>
      )}
      {/* QR Modal - Desktop only */}
      {showQR && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
          onClick={() => setShowQR(null)}
        >
          <div
            className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowQR(null)}
              className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2 text-center">
              {showQR === "instagram"
                ? "Seguinos en Instagram"
                : "Contactanos por WhatsApp"}
            </h3>
            <p className="text-[15px] text-neutral-500 mb-4 text-center">
              Escaneá el código QR con tu celular
            </p>
            <div className="flex justify-center mb-5">
              <img
                src={showQR === "instagram" ? instagramQR : whatsappQR}
                alt={
                  showQR === "instagram"
                    ? "QR Code Instagram"
                    : "QR Code WhatsApp"
                }
                className="w-48 h-48 rounded-md"
              />
            </div>
            {showQR === "whatsapp" && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-[var(--btn-h)] inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb855] text-white rounded-md font-medium text-[15px] shadow-sm transition-colors mb-2"
              >
                Abrir WhatsApp Web
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <button
              onClick={() => setShowQR(null)}
              className="w-full h-[var(--btn-h)] bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[15px] shadow-sm transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Ayuda;
